import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUpload, FaDownload, FaBook, FaFile } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import SEO from './SEO';
import "./Materials.css";
import ContentModerationService from '../services/ContentModerationService';
import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Sample materials data to show when no data is fetched from database
const sampleMaterials = [
  {
    id: 'sample-1',
    title: 'Advanced Calculus Study Guide',
    category: 'PDF',
    grade: 'Grade 12',
    subject: 'Mathematics',
    uploaderName: 'Math Teacher',
    uploadDate: new Date().toISOString(),
    fileName: 'calculus_guide.pdf',
    fileURL: '#',
    description: 'Comprehensive study guide for advanced calculus topics'
  },
  {
    id: 'sample-2',
    title: 'Physics Lab Experiment Templates',
    category: 'Document',
    grade: 'Grade 11',
    subject: 'Physics',
    uploaderName: 'Science Department',
    uploadDate: new Date(Date.now() - 86400000).toISOString(), // Yesterday
    fileName: 'physics_lab_templates.docx',
    fileURL: '#',
    description: 'Templates for documenting physics lab experiments'
  },
  {
    id: 'sample-3',
    title: 'English Literature Essay Examples',
    category: 'PDF',
    grade: 'Grade 10',
    subject: 'English',
    uploaderName: 'Literature Club',
    uploadDate: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
    fileName: 'essay_examples.pdf',
    fileURL: '#',
    description: 'Collection of well-written essay examples for literature studies'
  },
  {
    id: 'sample-4',
    title: 'Biology Final Exam Review',
    category: 'PowerPoint',
    grade: 'Grade 12',
    subject: 'Biology',
    uploaderName: 'Bio Teacher',
    uploadDate: new Date(Date.now() - 259200000).toISOString(), // 3 days ago
    fileName: 'biology_exam_review.pptx',
    fileURL: '#',
    description: 'Comprehensive review for final biology exam'
  }
];

const Materials = () => {
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [materials, setMaterials] = useState([]);
  const [userUploaded, setUserUploaded] = useState(true); // Set to true to enable downloads for sample data
  const [uploadForm, setUploadForm] = useState({ 
    title: '', 
    type: '', 
    grade: '', 
    subject: '',
    file: null 
  });
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [moderationStatus, setModerationStatus] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [usingSampleData, setUsingSampleData] = useState(false);

  const types = ['all', 'Exam', 'PowerPoint', 'Worksheet', 'Book', 'PDF', 'Document'];
  const grades = ['all', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];
  const subjects = ['all', 'Mathematics', 'Physics', 'English', 'Biology'];

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const materialsQuery = query(
        collection(db, 'materials'),
        orderBy('uploadDate', 'desc')
      );
      
      const snapshot = await getDocs(materialsQuery);
      const materialsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (materialsList.length > 0) {
        setMaterials(materialsList);
        setUserUploaded(true);
        setUsingSampleData(false);
      } else {
        // If no materials found in database, use sample data
        console.log("No materials found in database, using sample data");
        setMaterials(sampleMaterials);
        setUserUploaded(true);
        setUsingSampleData(true);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      // If there's an error fetching, use sample data
      console.log("Error fetching materials, using sample data");
      setMaterials(sampleMaterials);
      setUserUploaded(true);
      setUsingSampleData(true);
    }
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const handleTypeChange = (e) => setSelectedType(e.target.value);
  const handleGradeChange = (e) => setSelectedGrade(e.target.value);
  const handleSubjectChange = (e) => setSelectedSubject(e.target.value);

  const handleUploadChange = (e) => {
    if (e.target.name === 'file') {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 10 * 1024 * 1024) {
          setUploadError('File size should be less than 10MB');
          return;
        }
        const allowedTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/plain'
        ];
        if (!allowedTypes.includes(file.type)) {
          setUploadError('Invalid file type. Please upload PDF, Word, PowerPoint, Excel, or text files.');
          return;
        }
        setUploadForm(prev => ({ ...prev, file }));
      }
    } else {
      setUploadForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    }
    setUploadError('');
    setUploadSuccess('');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.title) {
      setUploadError('Please provide a title and file');
      return;
    }
    
    setUserUploaded(true);
    setUploadError('');
    setUploadProgress(0);
    setModerationStatus({ status: 'checking', message: 'Analyzing content...' });
    
    try {
      const contentText = await ContentModerationService.extractTextFromFile(uploadForm.file);
      
      const moderationResult = await ContentModerationService.moderateMaterial(
        contentText,
        uploadForm.title,
        uploadForm.description
      );
      
      if (!moderationResult.approved) {
        setModerationStatus({ 
          status: 'rejected', 
          message: `Upload rejected: ${moderationResult.reason}` 
        });
        
        await ContentModerationService.logModerationDecision(
          'material',
          'pre-upload',
          false,
          moderationResult.reason,
          currentUser?.uid || 'anonymous'
        );
        
        return;
      }
      
      setModerationStatus({ 
        status: 'approved', 
        message: 'Content approved, uploading...' 
      });
      
      const storageRef = ref(storage, `materials/${Date.now()}_${uploadForm.file.name}`);
      await uploadBytes(storageRef, uploadForm.file);
      
      const downloadURL = await getDownloadURL(storageRef);
      
      const materialData = {
        title: uploadForm.title,
        description: uploadForm.description || '',
        category: uploadForm.type,
        grade: uploadForm.grade,
        subject: uploadForm.subject,
        fileURL: downloadURL,
        fileName: uploadForm.file.name,
        fileType: uploadForm.file.type,
        fileSize: uploadForm.file.size,
        uploadDate: new Date().toISOString(),
        uploaderID: currentUser?.uid || 'anonymous',
        uploaderName: currentUser?.displayName || 'Anonymous User',
        moderationApproved: true,
        moderationReason: moderationResult.reason
      };
      
      const docRef = await addDoc(collection(db, 'materials'), materialData);
      
      await ContentModerationService.logModerationDecision(
        'material',
        docRef.id,
        true,
        moderationResult.reason,
        currentUser?.uid || 'anonymous'
      );
      
      setUploadSuccess('Material uploaded successfully!');
      setModerationStatus({ 
        status: 'success', 
        message: 'Material uploaded successfully!' 
      });
      
      setUploadForm({ title: '', type: '', grade: '', subject: '', file: null });
      setFileInputKey(Date.now());
      setUsingSampleData(false);
      
      fetchMaterials();
    } catch (error) {
      console.error('Error uploading material:', error);
      setUploadError('An error occurred during upload. Please try again.');
      setModerationStatus({ 
        status: 'error', 
        message: 'Error during upload process' 
      });
    }
  };

  const handleDownload = (material) => {
    if (usingSampleData) {
      alert("This is a sample material. In the real application, you would be able to download the file.");
      return;
    }
    window.open(material.fileURL, '_blank');
  };

  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || material.category === selectedType;
    const matchesGrade = selectedGrade === 'all' || material.grade === selectedGrade;
    const matchesSubject = selectedSubject === 'all' || material.subject === selectedSubject;
    return matchesSearch && matchesType && matchesGrade && matchesSubject;
  });

  return (
    <>
      <SEO 
        title="Study Materials - Student Helper"
        description="Access and share study materials, notes, and resources. A comprehensive collection of educational materials for students."
        keywords="study materials, educational resources, student notes, academic materials, learning resources"
      />
      <div className="materials-container">
        <motion.div className="upload-section modern-upload" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h2>Upload New Material</h2>
          {usingSampleData && (
            <div className="alert alert-info">
              Currently showing sample materials. Upload your own to get started!
            </div>
          )}
          <form className="upload-form" onSubmit={handleUpload}>
            <div className="form-row">
              <input 
                type="text" 
                name="title" 
                placeholder="Material Title" 
                value={uploadForm.title} 
                onChange={handleUploadChange} 
                className="upload-input" 
                required
              />
            </div>
            
            <div className="form-row">
              <select 
                name="type" 
                value={uploadForm.type} 
                onChange={handleUploadChange} 
                className="upload-select"
                required
              >
                <option value="">Select Type</option>
                {types.filter(type => type !== 'all').map(type => <option key={type} value={type}>{type}</option>)}
              </select>
              
              <select 
                name="grade" 
                value={uploadForm.grade} 
                onChange={handleUploadChange} 
                className="upload-select"
                required
              >
                <option value="">Select Grade</option>
                {grades.filter(grade => grade !== 'all').map(grade => <option key={grade} value={grade}>{grade}</option>)}
              </select>
            </div>
            
            <div className="form-row">
              <select 
                name="subject" 
                value={uploadForm.subject} 
                onChange={handleUploadChange} 
                className="upload-select"
                required
              >
                <option value="">Select Subject</option>
                {subjects.filter(subject => subject !== 'all').map(subject => <option key={subject} value={subject}>{subject}</option>)}
              </select>
            </div>
            
            <div className="file-upload-container">
              <input
                type="file"
                id="file"
                name="file"
                onChange={handleUploadChange}
                className="file-input"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
                key={fileInputKey}
                required
              />
              <label htmlFor="file" className="file-label">
                <FaFile /> {uploadForm.file ? uploadForm.file.name : 'Choose a file'}
              </label>
            </div>
            
            {moderationStatus && (
              <div className={`moderation-status ${moderationStatus.status}`}>
                {moderationStatus.message}
              </div>
            )}
            
            {uploadError && <div className="alert alert-error">{uploadError}</div>}
            {uploadSuccess && <div className="alert alert-success">{uploadSuccess}</div>}
            
            <div className="button-container">
              <button className="upload-button" type="submit" disabled={uploadProgress > 0}>
                <FaUpload /> Upload Material
              </button>
            </div>
          </form>
        </motion.div>

        <div className="materials-header">
          <h1>Study Materials</h1>
          <div className="search-filter-row">
            <input
              type="text"
              placeholder="Search materials..."
              value={searchQuery}
              onChange={handleSearch}
              className="search-input"
            />
            <select value={selectedType} onChange={handleTypeChange} className="filter-select">
              {types.map(type => (
                <option key={type} value={type}>{type === 'all' ? 'All Types' : type}</option>
              ))}
            </select>
            <select value={selectedGrade} onChange={handleGradeChange} className="filter-select">
              {grades.map(grade => (
                <option key={grade} value={grade}>{grade === 'all' ? 'All Grades' : grade}</option>
              ))}
            </select>
            <select value={selectedSubject} onChange={handleSubjectChange} className="filter-select">
              {subjects.map(subject => (
                <option key={subject} value={subject}>{subject === 'all' ? 'All Subjects' : subject}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredMaterials.length === 0 ? (
          <div className="no-results">No materials found. Try uploading some study materials!</div>
        ) : (
          <div className="materials-grid">
            <AnimatePresence>
              {filteredMaterials.map((material) => (
                <motion.div
                  key={material.id}
                  className="material-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="material-icon">
                    <FaBook size={24} style={{ color: '#0074e8', marginBottom: '10px' }} />
                  </div>
                  <h3>{material.title}</h3>
                  {material.description && (
                    <p className="material-description">{material.description}</p>
                  )}
                  <div className="material-info">
                    {material.category && <p><b>Type:</b> {material.category}</p>}
                    {material.grade && <p><b>Grade:</b> {material.grade}</p>}
                    {material.subject && <p><b>Subject:</b> {material.subject}</p>}
                    <p><b>Uploaded by:</b> {material.uploaderName || 'Anonymous User'}</p>
                    <p><b>Date:</b> {new Date(material.uploadDate).toLocaleDateString()}</p>
                    <p><b>File:</b> {material.fileName}</p>
                  </div>
                  <button 
                    className="download-button"
                    onClick={() => handleDownload(material)}
                  >
                    <FaDownload style={{marginRight: 8}}/>Download
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </>
  );
};

export default Materials;
